package br.com.vvrefeicoes.portal.feature.rh

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.ui.components.AppBanner
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppPagination
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.layout.AppHorizontalScrollbar
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.core.util.formatIsoDateTime
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.RhSheetColumn
import br.com.vvrefeicoes.portal.domain.model.RhSheetData
import br.com.vvrefeicoes.portal.domain.model.RhSheetKind
import com.composables.icons.lucide.FileSpreadsheet
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Trash2
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rh_action_cancel
import portalvv.shared.generated.resources.rh_action_delete
import portalvv.shared.generated.resources.rh_action_edit
import portalvv.shared.generated.resources.rh_action_save
import portalvv.shared.generated.resources.rh_delete_confirm
import portalvv.shared.generated.resources.rh_delete_message
import portalvv.shared.generated.resources.rh_delete_title
import portalvv.shared.generated.resources.rh_history_empty
import portalvv.shared.generated.resources.rh_history_title
import portalvv.shared.generated.resources.rh_history_updated_at
import portalvv.shared.generated.resources.rh_history_updated_by
import portalvv.shared.generated.resources.rh_import_sheet
import portalvv.shared.generated.resources.rh_replace_confirm
import portalvv.shared.generated.resources.rh_replace_message
import portalvv.shared.generated.resources.rh_replace_title
import portalvv.shared.generated.resources.rh_table_empty

@Composable
fun RhSheetTab(
    month: MonthPeriod,
    kind: RhSheetKind,
    columns: List<RhSheetColumn>,
    selectedUnit: String? = null,
    normalizeGrid: (List<List<String>>) -> List<List<String>> = { it },
) {
    val appGraph = LocalAppGraph.current
    val controller = rememberRhSheetController(appGraph.rhSheetRepository, kind, columns, normalizeGrid)
    LaunchedEffect(month) { controller.applyPeriod(month) }
    val table =
        (if (controller.editing) controller.draft else controller.data)
            ?.let { data -> if (controller.editing) data else data.filteredByUnit(columns, selectedUnit) }

    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            if (controller.editing) {
                AppButton(
                    text = stringResource(Res.string.rh_action_cancel),
                    onClick = controller::cancelEdit,
                    variant = PortalButtonVariant.Outlined,
                )
                AppButton(
                    text = stringResource(Res.string.rh_action_save),
                    onClick = controller::save,
                )
            } else {
                if (controller.data != null) {
                    AppButton(
                        text = stringResource(Res.string.rh_action_delete),
                        onClick = controller::requestDelete,
                        variant = PortalButtonVariant.Outlined,
                        leadingIcon = Lucide.Trash2,
                        enabled = !controller.deleting,
                    )
                    AppButton(
                        text = stringResource(Res.string.rh_action_edit),
                        onClick = controller::enterEdit,
                        variant = PortalButtonVariant.Outlined,
                    )
                }
                AppButton(
                    text = stringResource(Res.string.rh_import_sheet),
                    onClick = controller::requestImport,
                    variant = PortalButtonVariant.Outlined,
                    leadingIcon = Lucide.FileSpreadsheet,
                    enabled = !controller.importing,
                )
            }
        }

        controller.error?.let { error ->
            AppBanner(
                text = stringResource(error),
                onDismiss = controller::dismissError,
                isError = true,
            )
        }

        if (table == null) {
            EmptyState(message = stringResource(Res.string.rh_table_empty))
        } else {
            RhSheetTable(
                data = table,
                columns = columns,
                editing = controller.editing,
                onCellChange = controller::updateCell,
            )
            RhHistoryCard(updatedBy = controller.updatedBy, updatedAt = controller.updatedAt)
        }
    }

    if (controller.replaceConfirmOpen) {
        RhReplaceDialog(onConfirm = controller::confirmReplace, onDismiss = controller::dismissReplace)
    }

    controller.unitReview?.let { review ->
        RhUnitReviewDialog(
            review = review,
            saving = controller.savingUnits,
            onSelect = controller::selectUnit,
            onConfirm = controller::confirmUnitReview,
            onDismiss = controller::dismissUnitReview,
        )
    }

    if (controller.deleteConfirmOpen) {
        RhDeleteDialog(
            periodLabel = MonthCalendar.label(controller.period),
            onConfirm = controller::confirmDelete,
            onDismiss = controller::dismissDelete,
        )
    }
}

@Composable
private fun RhDeleteDialog(
    periodLabel: String,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
) {
    AppModalCard(title = stringResource(Res.string.rh_delete_title), onDismiss = onDismiss) {
        Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
            Text(text = stringResource(Res.string.rh_delete_message, periodLabel))
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
            ) {
                AppButton(
                    text = stringResource(Res.string.rh_action_cancel),
                    onClick = onDismiss,
                    variant = PortalButtonVariant.Outlined,
                )
                AppButton(
                    text = stringResource(Res.string.rh_delete_confirm),
                    onClick = onConfirm,
                )
            }
        }
    }
}

@Composable
private fun RhSheetTable(
    data: RhSheetData,
    columns: List<RhSheetColumn>,
    editing: Boolean,
    onCellChange: (rowIndex: Int, columnIndex: Int, value: String) -> Unit,
) {
    // A planilha pode ter milhares de linhas: desenhar todas de uma vez (ainda mais com um campo
    // de texto por célula, no modo de edição) trava a tela. Só a página atual é composta.
    var page by remember(data.period) { mutableStateOf(0) }
    val pageCount = (data.rows.size + PAGE_SIZE - 1) / PAGE_SIZE
    val currentPage = page.coerceIn(0, (pageCount - 1).coerceAtLeast(0))
    val firstRow = currentPage * PAGE_SIZE
    val visibleRows = data.rows.drop(firstRow).take(PAGE_SIZE)

    val scrollState = rememberScrollState()
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
                val viewportWidth = maxWidth
                val contentWidth = columns.fold(AppSpacing.space16) { acc, column -> acc + column.cellWidth() }
                Box(
                    modifier =
                        Modifier
                            .fillMaxWidth()
                            .horizontalScroll(scrollState),
                ) {
                    Column(modifier = Modifier.width(maxOf(viewportWidth, contentWidth))) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            columns.forEach { column ->
                                Text(
                                    text = column.label,
                                    style = MaterialTheme.typography.labelMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    maxLines = 1,
                                    modifier =
                                        Modifier
                                            .width(column.cellWidth())
                                            .padding(horizontal = AppSpacing.space8, vertical = AppSpacing.space12),
                                )
                            }
                        }
                        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                        visibleRows.forEachIndexed { index, row ->
                            val rowIndex = firstRow + index
                            // A chave mantém o texto em edição preso à linha certa ao trocar de página.
                            key(rowIndex) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    columns.forEachIndexed { columnIndex, column ->
                                        val value = row.getOrElse(columnIndex) { "" }
                                        if (editing && column.editable) {
                                            RhSheetEditCell(
                                                value = value,
                                                width = column.cellWidth(),
                                                onChange = { onCellChange(rowIndex, columnIndex, it) },
                                            )
                                        } else {
                                            Text(
                                                text = value,
                                                style = MaterialTheme.typography.bodySmall,
                                                maxLines = 1,
                                                modifier =
                                                    Modifier
                                                        .width(column.cellWidth())
                                                        .padding(horizontal = AppSpacing.space8, vertical = AppSpacing.space8),
                                            )
                                        }
                                    }
                                }
                            }
                            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                        }
                    }
                }
            }
            AppHorizontalScrollbar(
                state = scrollState,
                modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space8, vertical = AppSpacing.space4),
            )
            AppPagination(
                page = currentPage,
                pageCount = pageCount,
                onPageChange = { page = it.coerceIn(0, (pageCount - 1).coerceAtLeast(0)) },
                modifier = Modifier.align(Alignment.End).padding(AppSpacing.space8),
            )
        }
    }
}

@Composable
private fun RhSheetEditCell(
    value: String,
    width: Dp,
    onChange: (String) -> Unit,
) {
    var text by remember { mutableStateOf(value) }
    Surface(
        modifier = Modifier.width(width).padding(horizontal = AppSpacing.space4, vertical = AppSpacing.space4),
        shape = AppShapes.small,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline),
    ) {
        BasicTextField(
            value = text,
            onValueChange = { updated ->
                text = updated
                onChange(updated)
            },
            singleLine = true,
            textStyle = MaterialTheme.typography.bodySmall.copy(color = MaterialTheme.colorScheme.onSurface),
            cursorBrush = SolidColor(MaterialTheme.colorScheme.primary),
            modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space4, vertical = AppSpacing.space4),
        )
    }
}

@Composable
private fun RhReplaceDialog(
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
) {
    AppModalCard(title = stringResource(Res.string.rh_replace_title), onDismiss = onDismiss) {
        Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
            Text(text = stringResource(Res.string.rh_replace_message))
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
            ) {
                AppButton(
                    text = stringResource(Res.string.rh_action_cancel),
                    onClick = onDismiss,
                    variant = PortalButtonVariant.Outlined,
                )
                AppButton(
                    text = stringResource(Res.string.rh_replace_confirm),
                    onClick = onConfirm,
                )
            }
        }
    }
}

private fun RhSheetColumn.cellWidth(): Dp = if (editable) 120.dp else 150.dp

@Composable
private fun RhHistoryCard(
    updatedBy: String,
    updatedAt: String,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        ) {
            Text(
                text = stringResource(Res.string.rh_history_title),
                style = MaterialTheme.typography.titleMedium,
            )
            val text =
                when {
                    updatedBy.isNotBlank() ->
                        stringResource(Res.string.rh_history_updated_by, updatedBy, formatIsoDateTime(updatedAt))
                    updatedAt.isNotBlank() ->
                        stringResource(Res.string.rh_history_updated_at, formatIsoDateTime(updatedAt))
                    else -> stringResource(Res.string.rh_history_empty)
                }
            Text(
                text = text,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

/** Filtra as linhas exibidas pela unidade selecionada (coluna "unidade" da planilha). */
private fun RhSheetData.filteredByUnit(
    columns: List<RhSheetColumn>,
    unitName: String?,
): RhSheetData {
    if (unitName.isNullOrBlank()) return this
    val unitIndex = columns.indexOfFirst { it.header == UNIT_HEADER }
    if (unitIndex < 0) return this
    val filtered = rows.filter { row -> row.getOrElse(unitIndex) { "" }.equals(unitName, ignoreCase = true) }
    return copy(rows = filtered, costCenterIds = emptyList())
}

private const val UNIT_HEADER = "unidade"

private const val PAGE_SIZE = 25
